import {
    faArrowDown,
    faArrowLeft,
    faArrowRight,
    faArrowUp,
    faCrosshairs,
    faSearchMinus,
    faSearchPlus,
    faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState, type ChangeEvent, type MouseEvent, type TouchEvent } from "react";

type HoldAction =
    | "up"
    | "down"
    | "left"
    | "right"
    | "zoom_in"
    | "zoom_out";

export type CameraControlAction =
    | HoldAction
    | "reset"
    | "stop"
    | "autofocus"
    | "manual_focus";

export type CameraControlOptions = {
    enabled?: boolean;
    value?: number;
};

type Props = {
    onCommand: (action: CameraControlAction, options?: CameraControlOptions) => void;
    onExit: () => void;
};

const HOLD_INTERVAL_MS = 140;
const FOCUS_DEBOUNCE_MS = 100;
const SWAPPED_AXIS_ACTION: Record<HoldAction, HoldAction> = {
    up: "left",
    down: "right",
    left: "down",
    right: "up",
    zoom_in: "zoom_in",
    zoom_out: "zoom_out",
};

export default function RemoteControlPanel({ onCommand, onExit }: Props) {
    const holdTimer = useRef<number | null>(null);
    const focusTimer = useRef<number | null>(null);
    const activeAction = useRef<HoldAction | null>(null);
    const [autoFocus, setAutoFocus] = useState(true);
    const [manualFocus, setManualFocus] = useState(50);

    function clearHold() {
        if (holdTimer.current != null) {
            window.clearInterval(holdTimer.current);
            holdTimer.current = null;
        }
        if (activeAction.current) {
            onCommand("stop");
            activeAction.current = null;
        }
    }

    function startHold(action: HoldAction) {
        clearHold();
        const commandAction = SWAPPED_AXIS_ACTION[action];
        activeAction.current = commandAction;
        onCommand(commandAction);
        holdTimer.current = window.setInterval(() => {
            onCommand(commandAction);
        }, HOLD_INTERVAL_MS);
    }

    useEffect(() => {
        const end = () => clearHold();
        window.addEventListener("mouseup", end);
        window.addEventListener("touchend", end);
        window.addEventListener("blur", end);
        return () => {
            clearHold();
            if (focusTimer.current != null) {
                window.clearTimeout(focusTimer.current);
            }
            window.removeEventListener("mouseup", end);
            window.removeEventListener("touchend", end);
            window.removeEventListener("blur", end);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function padProps(action: HoldAction) {
        return {
            type: "button" as const,
            title: action.replace("_", " "),
            onMouseDown: (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                startHold(action);
            },
            onTouchStart: (e: TouchEvent) => {
                e.preventDefault();
                e.stopPropagation();
                startHold(action);
            },
            onContextMenu: (e: MouseEvent) => e.preventDefault(),
        };
    }

    function setFocusMode(enabled: boolean) {
        setAutoFocus(enabled);
        onCommand("autofocus", { enabled });
        if (!enabled) {
            onCommand("manual_focus", { value: manualFocus });
        }
    }

    function onManualFocusChanged(event: ChangeEvent<HTMLInputElement>) {
        const value = Number(event.target.value);
        setManualFocus(value);
        if (autoFocus) {
            setAutoFocus(false);
            onCommand("autofocus", { enabled: false });
        }
        if (focusTimer.current != null) {
            window.clearTimeout(focusTimer.current);
        }
        focusTimer.current = window.setTimeout(() => {
            onCommand("manual_focus", { value });
        }, FOCUS_DEBOUNCE_MS);
    }

    return (
        <div className="rcp" role="group" aria-label="HoloCam remote control">
            <div className="rcp-header">
                <div className="rcp-title">
                    <div className="rcp-title-text">
                        <strong>Camera Control</strong>
                        <span>HoloCam · PTZ</span>
                    </div>
                </div>
                <button
                    type="button"
                    className="rcp-exit"
                    title="Exit control"
                    onClick={(e) => {
                        e.stopPropagation();
                        clearHold();
                        onExit();
                    }}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>

            <div className="rcp-body">
                <div className="rcp-dpad" aria-label="Pan controls">
                    <button {...padProps("up")} className="rcp-pad-btn" style={{ gridArea: "up" }}>
                        <FontAwesomeIcon icon={faArrowUp} />
                    </button>
                    <button {...padProps("left")} className="rcp-pad-btn" style={{ gridArea: "left" }}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <button
                        type="button"
                        className="rcp-pad-btn rcp-reset-btn"
                        style={{ gridArea: "center" }}
                        title="Reset camera position"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            clearHold();
                            onCommand("reset");
                        }}
                    >
                        <span>Reset</span>
                    </button>
                    <button {...padProps("right")} className="rcp-pad-btn" style={{ gridArea: "right" }}>
                        <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                    <button {...padProps("down")} className="rcp-pad-btn" style={{ gridArea: "down" }}>
                        <FontAwesomeIcon icon={faArrowDown} />
                    </button>
                </div>

                <div className="rcp-zoom" aria-label="Zoom controls">
                    <button {...padProps("zoom_out")} className="rcp-zoom-btn">
                        <FontAwesomeIcon icon={faSearchMinus} />
                        <span>Zoom out</span>
                    </button>
                    <button {...padProps("zoom_in")} className="rcp-zoom-btn">
                        <FontAwesomeIcon icon={faSearchPlus} />
                        <span>Zoom in</span>
                    </button>
                </div>

                <div className="rcp-focus" aria-label="Focus controls">
                    <div className="rcp-focus-header">
                        <span className="rcp-focus-label">
                            <FontAwesomeIcon icon={faCrosshairs} />
                            Focus
                        </span>
                        <span className="rcp-focus-value">
                            {autoFocus ? "Auto" : `${manualFocus}%`}
                        </span>
                    </div>
                    <div className="rcp-focus-modes" role="group" aria-label="Focus mode">
                        <button
                            type="button"
                            className={`rcp-focus-mode${autoFocus ? " active" : ""}`}
                            aria-pressed={autoFocus}
                            onClick={(event) => {
                                event.stopPropagation();
                                setFocusMode(true);
                            }}
                        >
                            Auto
                        </button>
                        <button
                            type="button"
                            className={`rcp-focus-mode${!autoFocus ? " active" : ""}`}
                            aria-pressed={!autoFocus}
                            onClick={(event) => {
                                event.stopPropagation();
                                setFocusMode(false);
                            }}
                        >
                            Manual
                        </button>
                    </div>
                    <input
                        className="rcp-focus-slider"
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={manualFocus}
                        disabled={autoFocus}
                        aria-label="Manual focus"
                        aria-valuetext={`${manualFocus}%`}
                        onChange={onManualFocusChanged}
                        onClick={(event) => event.stopPropagation()}
                    />
                </div>
            </div>

            <style>{`
                .rcp {
                    width: 100%;
                    max-width: 300px;
                    box-sizing: border-box;
                    background: rgba(8, 15, 28, 0.92);
                    border: 1px solid rgba(255, 255, 255, 0.52);
                    border-radius: 18px;
                    backdrop-filter: blur(24px) saturate(1.15);
                    -webkit-backdrop-filter: blur(24px) saturate(1.15);
                    box-shadow:
                        0 12px 42px rgba(0, 0, 0, 0.58),
                        0 0 0 1px rgba(0, 0, 0, 0.48),
                        inset 0 1px 0 rgba(255, 255, 255, 0.18);
                    color: #f8fafc;
                    font-family: inherit;
                    overflow: hidden;
                    pointer-events: auto;
                    user-select: none;
                    -webkit-user-select: none;
                }

                .rcp-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    padding: 12px 14px 10px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
                    background: rgba(2, 6, 23, 0.58);
                    box-sizing: border-box;
                }

                .rcp-title {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    min-width: 0;
                }

                .rcp-title-text {
                    display: flex;
                    flex-direction: column;
                    gap: 1px;
                    min-width: 0;
                }

                .rcp-title-text strong {
                    color: #ffffff;
                    font-size: 0.86rem;
                    font-weight: 700;
                    letter-spacing: 0.01em;
                    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
                }

                .rcp-title-text span {
                    font-size: 0.68rem;
                    color: rgba(248, 250, 252, 0.82);
                    font-weight: 500;
                }

                .rcp-exit {
                    width: 32px;
                    height: 32px;
                    border-radius: 9px;
                    border: 1px solid rgba(255, 255, 255, 0.5);
                    background: rgba(15, 23, 42, 0.96);
                    color: #ffffff;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: background 0.15s, color 0.15s;
                    flex-shrink: 0;
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                }

                .rcp-exit:hover {
                    background: rgba(239, 68, 68, 0.28);
                    color: #fecaca;
                    border-color: rgba(252, 165, 165, 0.4);
                }

                .rcp-body {
                    display: flex;
                    flex-direction: column;
                    align-items: stretch;
                    gap: 12px;
                    padding: 14px;
                    box-sizing: border-box;
                }

                .rcp-dpad {
                    display: grid;
                    grid-template-areas:
                        ". up ."
                        "left center right"
                        ". down .";
                    grid-template-columns: repeat(3, 42px);
                    grid-template-rows: repeat(3, 42px);
                    gap: 6px;
                    margin: 0 auto;
                    justify-content: center;
                }

                .rcp-pad-btn {
                    width: 42px;
                    height: 42px;
                    border-radius: 12px;
                    border: 1px solid rgba(255, 255, 255, 0.68);
                    background: rgba(15, 23, 42, 0.96);
                    color: #ffffff;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.95rem;
                    transition: background 0.12s, border-color 0.12s, transform 0.08s;
                    touch-action: none;
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    box-shadow:
                        0 3px 9px rgba(0, 0, 0, 0.48),
                        inset 0 1px 0 rgba(255, 255, 255, 0.2);
                    box-sizing: border-box;
                    padding: 0;
                }

                .rcp-pad-btn:hover {
                    background: rgba(30, 64, 175, 0.96);
                    border-color: rgba(191, 219, 254, 0.95);
                    color: white;
                }

                .rcp-pad-btn svg,
                .rcp-zoom-btn svg {
                    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.72));
                }

                .rcp-pad-btn:active {
                    transform: scale(0.94);
                    background: rgba(37, 99, 235, 1);
                    border-color: #dbeafe;
                }

                .rcp-dpad-center,
                .rcp-reset-btn {
                    width: 42px;
                    height: 42px;
                    border-radius: 50%;
                    background: rgba(15, 23, 42, 0.96);
                    border: 1px solid rgba(255, 255, 255, 0.62);
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    color: #ffffff;
                    font-size: 0.58rem;
                    font-weight: 700;
                    letter-spacing: 0.04em;
                    text-transform: uppercase;
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    box-sizing: border-box;
                    padding: 0;
                    cursor: pointer;
                    transition: background 0.12s, border-color 0.12s, transform 0.08s;
                    touch-action: manipulation;
                    font-family: inherit;
                    box-shadow: 0 3px 9px rgba(0, 0, 0, 0.48);
                }

                .rcp-reset-btn:hover {
                    background: rgba(30, 64, 175, 0.96);
                    border-color: rgba(191, 219, 254, 0.95);
                    color: white;
                }

                .rcp-reset-btn:active {
                    transform: scale(0.94);
                    background: rgba(37, 99, 235, 0.45);
                    border-color: rgba(147, 197, 253, 0.55);
                }

                .rcp-zoom {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 8px;
                    width: 100%;
                    box-sizing: border-box;
                }

                .rcp-zoom-btn {
                    width: 100%;
                    min-width: 0;
                    height: 40px;
                    border-radius: 11px;
                    border: 1px solid rgba(255, 255, 255, 0.62);
                    background: rgba(15, 23, 42, 0.96);
                    color: #ffffff;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    font-size: 0.75rem;
                    font-weight: 650;
                    font-family: inherit;
                    transition: background 0.12s, border-color 0.12s, transform 0.08s;
                    touch-action: none;
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    box-shadow:
                        0 3px 9px rgba(0, 0, 0, 0.42),
                        inset 0 1px 0 rgba(255, 255, 255, 0.18);
                    box-sizing: border-box;
                    padding: 0 8px;
                }

                .rcp-zoom-btn span {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .rcp-zoom-btn:hover {
                    background: rgba(30, 64, 175, 0.96);
                    border-color: rgba(191, 219, 254, 0.95);
                    color: white;
                }

                .rcp-zoom-btn:active {
                    transform: scale(0.96);
                    background: rgba(37, 99, 235, 1);
                    border-color: #dbeafe;
                }

                .rcp-focus {
                    display: flex;
                    flex-direction: column;
                    gap: 9px;
                    padding: 11px;
                    border: 1px solid rgba(255, 255, 255, 0.34);
                    border-radius: 12px;
                    background: rgba(2, 6, 23, 0.68);
                    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
                }

                .rcp-focus-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                }

                .rcp-focus-label {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    color: #f8fafc;
                    font-size: 0.76rem;
                    font-weight: 700;
                }

                .rcp-focus-value {
                    color: #bfdbfe;
                    font-size: 0.69rem;
                    font-weight: 700;
                    font-variant-numeric: tabular-nums;
                }

                .rcp-focus-modes {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 4px;
                    padding: 3px;
                    border: 1px solid rgba(255, 255, 255, 0.28);
                    border-radius: 9px;
                    background: rgba(15, 23, 42, 0.92);
                }

                .rcp-focus-mode {
                    height: 30px;
                    border: 0;
                    border-radius: 6px;
                    background: transparent;
                    color: rgba(248, 250, 252, 0.72);
                    cursor: pointer;
                    font-family: inherit;
                    font-size: 0.69rem;
                    font-weight: 700;
                    transition: background 0.14s, color 0.14s, box-shadow 0.14s;
                }

                .rcp-focus-mode:hover {
                    color: #ffffff;
                    background: rgba(51, 65, 85, 0.82);
                }

                .rcp-focus-mode.active {
                    color: #ffffff;
                    background: #2563eb;
                    box-shadow: 0 2px 7px rgba(0, 0, 0, 0.36);
                }

                .rcp-focus-slider {
                    width: 100%;
                    height: 18px;
                    margin: 0;
                    accent-color: #60a5fa;
                    cursor: pointer;
                }

                .rcp-focus-slider:disabled {
                    cursor: not-allowed;
                    opacity: 0.38;
                }

                @media (max-width: 480px) {
                    .rcp {
                        max-width: 280px;
                    }
                    .rcp-body {
                        gap: 10px;
                        padding: 12px;
                    }
                    .rcp-dpad {
                        grid-template-columns: repeat(3, 38px);
                        grid-template-rows: repeat(3, 38px);
                    }
                    .rcp-pad-btn,
                    .rcp-reset-btn {
                        width: 38px;
                        height: 38px;
                    }
                    .rcp-zoom-btn {
                        height: 38px;
                        font-size: 0.7rem;
                        gap: 5px;
                    }
                }
            `}</style>
        </div>
    );
}
